/**
 * Hook for fetching cache analytics stats
 */

import { useAuth } from '@contexts/AuthContext';
import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../config/api';

// Cache type IDs
export type CacheType =
  | 'oddsGames'
  | 'oddsProps'
  | 'playerStats'
  | 'playerId'
  | 'allPlayers'
  | 'gameScript'
  | 'teamBase'
  | 'teamMisc'
  | 'shotDefense'
  | 'teamRecord'
  | 'picks';

export interface CacheStat {
  id: CacheType;
  name: string;
  description: string;
  source: 'Odds API' | 'BallDontLie' | 'Internal';
  ttlMs: number;
  ttlDisplay: string;
  entries: number;
  hits: number;
  misses: number;
  hitRate: number;
  lastUpdated: string | null;
}

interface CacheStatsResponse {
  caches: CacheStat[];
  quota: {
    quotaRemaining: number | null;
    quotaUsed: number | null;
  };
}

interface UseCacheStatsResult {
  caches: CacheStat[];
  quota: {
    quotaRemaining: number | null;
    quotaUsed: number | null;
  };
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearSelected: (cacheIds: CacheType[]) => Promise<boolean>;
  clearAll: () => Promise<boolean>;
  clearing: boolean;
}

export function useCacheStats(): UseCacheStatsResult {
  const { authFetch } = useAuth();
  const [caches, setCaches] = useState<CacheStat[]>([]);
  const [quota, setQuota] = useState<{
    quotaRemaining: number | null;
    quotaUsed: number | null;
  }>({
    quotaRemaining: null,
    quotaUsed: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  const fetchStats = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await authFetch(apiUrl('/api/admin/cache/stats'));

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Unauthorized - admin access required');
        }
        throw new Error('Failed to fetch cache stats');
      }

      const data: CacheStatsResponse = await response.json();
      setCaches(data.caches);
      setQuota(data.quota);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  const clearSelected = useCallback(
    async (cacheIds: CacheType[]): Promise<boolean> => {
      setClearing(true);
      try {
        const response = await authFetch(apiUrl('/api/admin/cache/clear-selected'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cacheIds }),
        });

        if (!response.ok) {
          throw new Error('Failed to clear caches');
        }

        // Refresh stats after clearing
        await fetchStats();
        return true;
      } catch (_err) {
        return false;
      } finally {
        setClearing(false);
      }
    },
    [authFetch, fetchStats],
  );

  const clearAll = useCallback(async (): Promise<boolean> => {
    setClearing(true);
    try {
      const response = await authFetch(apiUrl('/api/admin/cache/clear'), {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Failed to clear all caches');
      }

      // Refresh stats after clearing
      await fetchStats();
      return true;
    } catch (_err) {
      return false;
    } finally {
      setClearing(false);
    }
  }, [authFetch, fetchStats]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return {
    caches,
    quota,
    loading,
    error,
    refetch: fetchStats,
    clearSelected,
    clearAll,
    clearing,
  };
}
